import React from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';

/**
 * One Vasco document row, shared by the B2B order detail page and the payment
 * detail page so both list Vasco invoices the same way. The caller supplies the
 * chips, the amount block and the optional action button, because those differ
 * per page; everything else (colour, icon, "Vasco NNNN/YYYY", buyer) is fixed
 * here.
 */

export type VascoChipTone = 'gray' | 'green' | 'amber' | 'purple' | 'blue';

const CHIP_CLASSES: Record<VascoChipTone, string> = {
  gray: 'bg-gray-100 text-gray-700',
  green: 'bg-green-100 text-green-800',
  amber: 'bg-amber-100 text-amber-800',
  purple: 'bg-purple-100 text-purple-800',
  blue: 'bg-blue-100 text-blue-800',
};

export const VascoChip: React.FC<{
  tone?: VascoChipTone;
  title?: string;
  children: React.ReactNode;
}> = ({ tone = 'gray', title, children }) => (
  <span
    title={title}
    className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${CHIP_CLASSES[tone]}`}
  >
    {children}
  </span>
);

interface VascoDocumentRowProps {
  /** Verified means the number exists in the Vasco import: green row, else amber. */
  verified: boolean;
  invoiceNumber: string;
  buyer?: string | null;
  /** VascoChip elements shown after the buyer. */
  chips?: React.ReactNode;
  /** Right-hand amount block. */
  amount?: React.ReactNode;
  /** Right-hand button, e.g. Remove. */
  action?: React.ReactNode;
  testId?: string;
}

const VascoDocumentRow: React.FC<VascoDocumentRowProps> = ({
  verified,
  invoiceNumber,
  buyer,
  chips,
  amount,
  action,
  testId,
}) => (
  <div
    data-testid={testId}
    className={`flex items-center justify-between py-2 px-3 border rounded-md ${
      verified ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
    }`}
  >
    <div className="flex items-center gap-3 min-w-0 flex-wrap">
      {verified ? (
        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
      )}
      <span className="font-medium text-sm text-gray-800">Vasco {invoiceNumber}</span>
      {buyer && <span className="text-sm text-gray-600 truncate">{buyer}</span>}
      {chips}
    </div>
    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
      {amount}
      {action}
    </div>
  </div>
);

export default VascoDocumentRow;
